import React from 'react';
import classNames from 'classnames';
import { Checkbox } from '@headlessui/react';

import Icon from './Icon';

type RadioBulletProps<T extends string> = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'title' | 'ref' | 'value' | 'onChange'> & {
  description: React.ReactNode;
  checked: boolean;
  onChange: (value: T) => void;
  title: React.ReactNode;
  value: T;
};

const RadioBullet = <T extends string>({
  value, checked, onChange, title, description, ...inputProps
}: RadioBulletProps<T>) => {
  const id = `item-${value}`;
  const handleChange = () => {
    onChange(value);
  };

  return (
    <div
      className={classNames(
        'flex items-center justify-between w-full p-5 kiosk:p-10 bg-card-radio-bg rounded-card border-card-radio-size border-card-radio-border cursor-pointer my-2xs duration-500',
        {
          'hover:bg-card-radio-hover-bg has-hover:hover:border-btn-primary-hover-border': !checked,
          '!bg-card-radio-active-bg hover:bg-card-radio-active-bg cursor-default !border-card-radio-active-border': checked,
        },
      )}
      data-testid="radiobullet-wrapper"
      onClick={handleChange}
    >
      <label htmlFor={id} className="flex flex-col w-full cursor-pointer">
        <span
          className={classNames(
            'text-xl font-bold text-card-radio-title',
            { '!text-card-radio-active-title': checked },
          )}
        >
          {title}
        </span>
        <span
          className={classNames(
            'text-card-radio-description',
            { '!text-card-radio-active-description': checked },
          )}
        >
          {description}
        </span>
      </label>

      <Checkbox
        checked={checked}
        onChange={handleChange}
        className="cursor-pointer group size-6 border-radio-size border-radio-border data-checked:bg-radio-active-bg data-checked:border-radio-active-border flex items-center justify-center rounded-full shrink-0"
        {...inputProps}
      >
        {checked && <Icon className="fb-icon--check text-button-xs text-radio-active-dot" />}
      </Checkbox>
    </div>
  );
};

export default RadioBullet;
